import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { TRANSLATION_PORTAL } from '@contracts/translations'
import { useServices } from '@/shared/services'
import { useLanguage } from './hooks'
import { useLocaleStore } from './locale-store'
import { createTranslationsApi } from './translations.api'

/** First element of every translations query key; a language switch must not invalidate these. */
export const TRANSLATIONS_QUERY_SCOPE = 'translations'

/**
 * Loads the catalogue of the chosen language and mirrors it into the locale store, where `t()`
 * reads it. Mounted once, near the root. Nothing waits on it: the bundled English (or the
 * catalogue persisted by an earlier visit) is on screen until the answer arrives.
 */
export function useTranslations() {
  const { http } = useServices()
  const { language } = useLanguage()
  const setCatalogue = useLocaleStore((state) => state.setCatalogue)

  const query = useQuery({
    // The language is part of the key: each catalogue is cached on its own.
    queryKey: [TRANSLATIONS_QUERY_SCOPE, TRANSLATION_PORTAL, language],
    queryFn: ({ signal }) => createTranslationsApi(http).catalogue(language, signal),
    staleTime: 60 * 60 * 1000,
    gcTime: Infinity,
  })

  const catalogue = query.data
  useEffect(() => {
    // An empty catalogue is not written: it would replace a good one with nothing.
    if (!catalogue || Object.keys(catalogue.resources).length === 0) return
    setCatalogue(catalogue.language, catalogue.resources)
  }, [catalogue, setCatalogue])

  return query
}
